import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Partner, PartnerType, UpdatePartnerRequest, BankAccount } from '../../models/partner';
import { PartnersService } from '../../../services/partners.service';


@Component({
  selector: 'app-edit-partner',
  templateUrl: './edit-partner.component.html',
  styleUrls: ['./edit-partner.component.scss']
})
export class EditPartnerComponent implements OnInit {
  partner: Partner | null = null;
  partnerForm: any = {
    contact: {
      name: '',
      email: '',
      phoneNumber: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: ''
      }
    },
    faxNo: '',
    bankAccounts: []
  };

  loading = false;
  saving = false;
  error: string | null = null;
  success = false;
  partnerId: string | null = null;
  logoAttachment: { id?: string; fileName?: string; extension?: string; url?: string; } | undefined = undefined;
  pendingLogoAttachment: { id: string; fileName: string } | undefined = undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private partnersService: PartnersService

  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.partnerId = params.get('id');
      if (this.partnerId) {
        this.loadPartner(this.partnerId);
      } else {
        this.error = 'No partner ID provided';
      }
    });
  }

  loadPartner(id: string): void {
    this.loading = true;
    this.error = null;

    this.partnersService.getPartnerById(id).subscribe({
      next: (partner: Partner) => {
        this.partner = partner;
        // Populate the form with existing partner data
        this.partnerForm = {
          contact: {
            name: partner.contact?.name || '',
            email: partner.contact?.email || '',
            phoneNumber: partner.contact?.phoneNumber || '',
            address: {
              street: partner.contact?.address?.street || '',
              city: partner.contact?.address?.city || '',
              state: partner.contact?.address?.state || '',
              zip: partner.contact?.address?.zip || ''
            }
          },
          faxNo: partner.faxNo || '',
          bankAccounts: partner.bankAccounts && partner.bankAccounts.length > 0 
            ? [...partner.bankAccounts] 
            : []
        };
        // Set logo attachment if exists
        this.logoAttachment = partner.logoAttachment || undefined;
        console.log('Loaded partner logo attachment:', this.logoAttachment);
        if (this.logoAttachment?.url) {
          console.log('Logo URL:', this.logoAttachment.url);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading partner:', error);
        if (error.status === 404) {
          this.error = 'Partner not found';
        } else if (error.status === 401) {
          this.error = 'You are not authorized to edit this partner';
        } else if (error.status === 403) {
          this.error = 'Access denied to edit this partner';
        } else {
          this.error = 'Failed to load partner details. Please try again.';
        }
        this.loading = false;
      }
    });
  }

  onSubmit(): void {
    if (!this.partnerForm.contact.name?.trim()) {
      this.error = 'Partner name is required';
      return;
    }

    if (!this.partnerForm.contact.email?.trim()) {
      this.error = 'Email is required';
      return;
    }

    if (!this.partnerForm.contact.phoneNumber?.trim()) {
      this.error = 'Phone number is required';
      return;
    }

    if (!this.partnerId) {
      this.error = 'Partner ID not found';
      return;
    }

    this.saving = true;
    this.error = null;

    const updateData: UpdatePartnerRequest = {
      contact: {
        name: this.partnerForm.contact.name.trim(),
        phoneNumber: this.partnerForm.contact.phoneNumber.trim(),
        email: this.partnerForm.contact.email.trim(),
        address: {
          street: this.partnerForm.contact.address.street.trim(),
          city: this.partnerForm.contact.address.city.trim(),
          state: this.partnerForm.contact.address.state.trim(),
          zip: this.partnerForm.contact.address.zip.trim()
        }
      },
      type: this.partner?.type || PartnerType.DENTIST,
      faxNo: this.partnerForm.faxNo?.trim() || undefined,
      bankAccounts: this.partnerForm.bankAccounts && this.partnerForm.bankAccounts.length > 0 
        ? this.partnerForm.bankAccounts.filter((ba: BankAccount) => ba.name?.trim() || ba.number?.trim())
        : undefined,
      logoAttachment: this.pendingLogoAttachment || (this.logoAttachment ? {
        id: this.logoAttachment.id || '',
        fileName: this.logoAttachment.fileName || ''
      } : undefined)
    };

    console.log('🚀 Updating partner with data:', updateData);
    console.log('🚀 Pending logo attachment:', this.pendingLogoAttachment);
    console.log('🚀 Existing logo attachment:', this.logoAttachment);
    console.log('🚀 Final logoAttachment in request:', updateData.logoAttachment);

    this.partnersService.updatePartner(this.partnerId, updateData).subscribe({
      next: (updatedPartner) => {
        console.log('Partner updated successfully:', updatedPartner);
        this.success = true;
        this.saving = false;
        
        // Redirect to partner details after 2 seconds
        setTimeout(() => {
          this.router.navigate(['/partners', this.partnerId]);
        }, 2000);
      },
      error: (error) => {
        console.error('Error updating partner:', error);
        this.error = error.error?.message || 'Failed to update partner. Please try again.';
        this.saving = false;
      }
    });
  }

  onCancel(): void {
    if (this.partnerId) {
      this.router.navigate(['/partners', this.partnerId]);
    } else {
      this.router.navigate(['/partners']);
    }
  }

  resetForm(): void {
    if (this.partner) {
      this.partnerForm = {
        contact: {
          name: this.partner.contact?.name || '',
          email: this.partner.contact?.email || '',
          phoneNumber: this.partner.contact?.phoneNumber || '',
          address: {
            street: this.partner.contact?.address?.street || '',
            city: this.partner.contact?.address?.city || '',
            state: this.partner.contact?.address?.state || '',
            zip: this.partner.contact?.address?.zip || ''
          }
        },
        faxNo: this.partner.faxNo || '',
        bankAccounts: this.partner.bankAccounts && this.partner.bankAccounts.length > 0 
          ? [...this.partner.bankAccounts] 
          : []
      };
    }
    this.error = null;
    this.success = false;
    this.pendingLogoAttachment = undefined;
  }

  addBankAccount(): void {
    if (!this.partnerForm.bankAccounts) {
      this.partnerForm.bankAccounts = [];
    }
    this.partnerForm.bankAccounts.push({ name: '', number: '' });
  }

  removeBankAccount(index: number): void {
    if (this.partnerForm.bankAccounts && index >= 0 && index < this.partnerForm.bankAccounts.length) {
      this.partnerForm.bankAccounts.splice(index, 1);
    }
  }

  getPartnerTypeLabel(): string {
    switch (this.partner?.type) {
      case PartnerType.DENTIST: return 'Dentist';
      case PartnerType.TECH: return 'Tech';
      case PartnerType.COURIER: return 'Courier';
      case PartnerType.SUPPLIER: return 'Supplier';
      default: return 'Unknown';
    }
  }

  onLogoFileSelected(file: File): void {
    // File upload is now handled by the file-upload component
    // This method is kept for compatibility but the actual upload logic
    // is now in the file-upload component
    console.log('📁 File selected:', file.name);
  }

  onLogoUploadCompleted(data: { id: number; fileName: string }): void {
    console.log('🔥 onLogoUploadCompleted called with data:', data);
    // Store the attachment ID and file name for later use when saving the partner
    this.pendingLogoAttachment = {
      id: data.id.toString(),
      fileName: data.fileName
    };
    console.log('🎯 Logo uploaded successfully, waiting for partner save. Attachment ID:', data.id);
    console.log('🎯 File name:', data.fileName);
    console.log('🎯 Pending logo attachment set to:', this.pendingLogoAttachment);
    console.log('🎯 Current pendingLogoAttachment value:', this.pendingLogoAttachment);
  }

  onLogoUploadError(error: string): void {
    console.log('🔥 onLogoUploadError called with error:', error);
    this.error = error;
  }

  onLogoRemoved(): void {
    console.log('🔥 onLogoRemoved called');
    this.logoAttachment = undefined;
    this.pendingLogoAttachment = undefined;
  }
}
